import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { PdrReasoningBlock } from "./PdrReasoningBlock";
import { Button } from "./ui";
import { cn } from "../lib/cn";
import {
  fieldLabel,
  fieldQuote,
  fieldReasoning,
  fieldSourceUrl,
  fieldValue,
  tableFieldKeys,
  type ProjectData,
} from "../lib/pdrFields";
import type { EvalResultSnapshot } from "../lib/evalPlaygroundStorage";
import type { OpenRouterModel } from "../lib/types";

const LONG_TEXT_KEYS = new Set([
  "project_description",
  "project_roadmap",
  "project_risk_factors",
  "project_objective",
  "project_challenge",
  "project_accuracy_justification",
  "project_accuracy_unverified_claims",
]);

const METRIC_COL_PX = 208;
/** Expanded model columns share the table width and only scroll sideways below this, so
 *  five of them still fit beside the metric column on a laptop screen. */
const MIN_MODEL_COL_PX = 180;

function modelLabel(modelId: string, catalog: OpenRouterModel[]): string {
  const hit = catalog.find((m) => m.id === modelId);
  return hit?.name || modelId.split("/").pop() || modelId;
}

function modelPrice(modelId: string, catalog: OpenRouterModel[]): string {
  const hit = catalog.find((m) => m.id === modelId);
  if (hit?.prompt_per_million) {
    return `$${hit.prompt_per_million}/M tokens`;
  }
  return "";
}

function cellValue(
  data: ProjectData | null | undefined,
  key: string,
  expanded: boolean,
): string {
  if (!data) return "—";
  const val = fieldValue(data, key);
  if (!expanded && LONG_TEXT_KEYS.has(key) && val.length > 120) {
    return `${val.slice(0, 120)}…`;
  }
  return val || "—";
}

/** Per row: flags values that disagree with the modal (most common) answer, so outliers stand out. */
function rowDisagreements(values: string[]): boolean[] {
  const normalized = values.map((v) => v.trim());
  if (normalized.every((v) => v === normalized[0])) {
    return normalized.map(() => false);
  }
  const counts = new Map<string, number>();
  for (const v of normalized) counts.set(v, (counts.get(v) ?? 0) + 1);
  let mode = normalized[0];
  let modeCount = 0;
  for (const v of normalized) {
    const count = counts.get(v) ?? 0;
    if (count > modeCount) {
      modeCount = count;
      mode = v;
    }
  }
  return normalized.map((v) => v !== mode);
}

function statusBadge(passed: boolean, error?: string) {
  if (passed) {
    return (
      <span className="text-secondary font-bold uppercase text-[11px] tracking-wide">
        Valid
      </span>
    );
  }
  return (
    <span className="text-error font-bold uppercase text-[11px] tracking-wide" title={error}>
      Failed
    </span>
  );
}

/** Wrap long field text inside a fixed column — grows downward, not sideways. */
function CellBody({ children, expanded }: { children: ReactNode; expanded: boolean }) {
  if (!expanded) return <>{children}</>;
  return (
    <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] w-full min-w-0">
      {children}
    </div>
  );
}

type Props = {
  results: EvalResultSnapshot[];
  catalog: OpenRouterModel[];
  expanded?: boolean;
  pdrProfile?: string;
};

export function EvalTableExpandToggle({
  expanded,
  onToggle,
  className = "",
}: {
  expanded: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <Button
      variant={expanded ? "outline-primary" : "outline-primary"}
      size="sm"
      onClick={onToggle}
      className={cn(expanded && "bg-primary/15 border-primary/40", className)}
      title={
        expanded
          ? "Truncate columns and fit table to viewport"
          : "Show full text in equal-width columns; scrolls sideways only when they do not fit"
      }
    >
      <Icon name={expanded ? "unfold_less" : "unfold_more"} className="text-base" />
      {expanded ? "Compact table" : "Expand all columns"}
    </Button>
  );
}

export function EvalResultsTable({
  results,
  catalog,
  expanded = false,
  pdrProfile,
}: Props) {
  if (results.length === 0) return null;

  const fieldKeys = tableFieldKeys(pdrProfile, results);

  const tableMinWidthPx = expanded
    ? METRIC_COL_PX + results.length * MIN_MODEL_COL_PX
    : undefined;

  const metricCell = expanded
    ? "p-3 font-body-sm text-on-surface-variant bg-surface-container-low border-r border-outline-variant sticky left-0 z-[5] align-top shadow-[4px_0_8px_-4px_rgba(0,0,0,0.35)]"
    : "p-3 font-body-sm text-on-surface-variant bg-surface-container-low border-r border-outline-variant";

  const modelCell = (i: number, extra = "") =>
    cn(
      "p-3 border-r border-outline-variant align-top last:border-r-0",
      i % 2 === 1 && "bg-[#0a0a0a]/40",
      expanded ? "text-left min-w-0" : "text-center",
      extra,
    );

  return (
    <table
      className="w-full table-fixed border-collapse"
      style={tableMinWidthPx ? { minWidth: tableMinWidthPx } : undefined}
    >
      {expanded ? (
        <colgroup>
          <col style={{ width: METRIC_COL_PX }} />
          {results.map((r) => (
            <col key={r.model} />
          ))}
        </colgroup>
      ) : null}
      <thead className="sticky top-0 z-10 bg-surface-container-high/95 backdrop-blur-sm">
        <tr className="border-b border-outline-variant">
          <th
            className={cn(
              "p-4 text-left font-data-label uppercase tracking-widest text-on-surface-variant border-r border-outline-variant",
              expanded &&
                "sticky left-0 z-[11] bg-surface-container-high shadow-[4px_0_8px_-4px_rgba(0,0,0,0.35)]",
              !expanded && "w-48",
            )}
          >
            Metric
          </th>
          {results.map((r, i) => (
            <th
              key={r.model}
              className={cn(
                "p-4 border-r border-outline-variant last:border-r-0",
                expanded ? "text-left" : "text-center",
                i % 2 === 1 && "bg-[#0a0a0a]/40",
              )}
              style={expanded ? undefined : { width: `${100 / results.length}%` }}
            >
              <div
                className={cn(
                  "flex flex-col gap-1 min-w-0 [overflow-wrap:anywhere]",
                  expanded ? "items-start" : "items-center",
                )}
              >
                <span className="font-headline-md text-primary text-sm">
                  {modelLabel(r.model, catalog)}
                </span>
                <span className="text-[10px] font-data-label text-on-surface-variant">
                  {modelPrice(r.model, catalog) || r.model}
                </span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-outline-variant font-data-mono text-data-mono text-sm">
        <tr>
          <td className={metricCell}>Model ID</td>
          {results.map((r, i) => (
            <td
              key={`${r.model}-id`}
              className={modelCell(i, "text-primary text-xs")}
            >
              <CellBody expanded={expanded}>
                <span className="break-all">{r.model}</span>
              </CellBody>
            </td>
          ))}
        </tr>
        <tr>
          <td className={metricCell}>Schema validation</td>
          {results.map((r, i) => (
            <td key={`${r.model}-val`} className={modelCell(i)}>
              <CellBody expanded={expanded}>
                {statusBadge(r.validation_passed, r.error)}
                {!r.validation_passed && r.validation_errors?.length ? (
                  <p
                    className={cn(
                      "text-[10px] text-error mt-1 text-left",
                      !expanded && "max-w-xs mx-auto",
                    )}
                  >
                    {(expanded
                      ? r.validation_errors
                      : r.validation_errors.slice(0, 3)
                    ).join(expanded ? "\n" : "; ")}
                  </p>
                ) : null}
              </CellBody>
            </td>
          ))}
        </tr>
        {results.some((r) => (r.calls_planned ?? 1) > 1) ? (
          <tr>
            <td className={metricCell}>Calls made</td>
            {results.map((r, i) => (
              <td
                key={`${r.model}-calls`}
                className={modelCell(
                  i,
                  (r.calls_made ?? 0) < (r.calls_planned ?? 1) ? "text-error" : "",
                )}
                title="Successive synthesis calls, each updating the PDR with the next batch of documents"
              >
                {r.calls_made ?? 0} / {r.calls_planned ?? 1}
              </td>
            ))}
          </tr>
        ) : null}
        <tr>
          <td className={metricCell}>Latency</td>
          {results.map((r, i) => (
            <td key={`${r.model}-lat`} className={modelCell(i, "text-secondary")}>
              {(r.latency_ms / 1000).toFixed(2)}s
            </td>
          ))}
        </tr>
        <tr>
          <td className={metricCell}>Cost</td>
          {results.map((r, i) => (
            <td
              key={`${r.model}-cost`}
              className={modelCell(i)}
              title={
                r.cost_billed
                  ? "Billed by OpenRouter"
                  : "Estimate: priced at the model's input rate, so it undercounts output"
              }
            >
              {r.cost_billed ? "" : "≈"}${r.cost_estimate.toFixed(4)}
            </td>
          ))}
        </tr>
        {fieldKeys.map((fieldKey) => {
          const disagreements = rowDisagreements(
            results.map((r) => fieldValue(r.generated_pdr || {}, fieldKey)),
          );
          return (
            <tr key={fieldKey}>
              <td className={metricCell}>{fieldLabel(fieldKey)}</td>
              {results.map((r, i) => {
                const val = cellValue(r.generated_pdr, fieldKey, expanded);
                const reasoning = fieldReasoning(r.generated_pdr || {}, fieldKey);
                const quote = fieldQuote(r.generated_pdr || {}, fieldKey);
                const sourceUrl = fieldSourceUrl(r.generated_pdr || {}, fieldKey);
                const disagrees = disagreements[i];
                const highlight =
                  fieldKey === "deal_score" && val !== "—" ? (
                    <span className="bg-secondary/20 text-secondary px-2 py-0.5 rounded font-bold">
                      {val}
                    </span>
                  ) : fieldKey === "project_risk_level" ? (
                    <span className="bg-tertiary/20 text-tertiary px-2 py-0.5 rounded">
                      {val}
                    </span>
                  ) : (
                    <span className="text-on-surface/80 text-xs leading-relaxed">{val}</span>
                  );
                return (
                  <td key={`${r.model}-${fieldKey}`} className={modelCell(i)}>
                    <CellBody expanded={expanded}>
                      <span
                        className={cn(
                          disagrees && "ring-1 ring-error/50 bg-error/10 rounded px-1 -mx-1",
                        )}
                        title={disagrees ? "Differs from the majority answer" : undefined}
                      >
                        {highlight}
                      </span>
                    </CellBody>
                    {reasoning ? (
                      <CellBody expanded={expanded}>
                        <PdrReasoningBlock
                          text={reasoning}
                          quote={quote}
                          sourceUrl={sourceUrl}
                        />
                      </CellBody>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function EvalResultsLoading({ modelCount }: { modelCount: number }) {
  return (
    <div className="p-8 text-center text-primary">
      <Icon name="sync" className="animate-spin text-3xl" />
      <p className="mt-2 font-data-label uppercase tracking-widest">
        Fan-out to {modelCount} models…
      </p>
      <p className="mt-1 text-body-sm text-on-surface-variant">
        Each model reads the ticked documents in successive calls; its column appears when it
        finishes.
      </p>
    </div>
  );
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Status line for a background run: who has finished, who is still reading, time elapsed. */
export function EvalRunProgress({
  models,
  finished,
  startedAt,
  now,
  catalog,
}: {
  models: string[];
  finished: EvalResultSnapshot[];
  startedAt: number;
  now: number;
  catalog: OpenRouterModel[];
}) {
  const done = new Set(finished.map((r) => r.model));
  const waiting = models.filter((m) => !done.has(m));
  return (
    <div className="px-4 py-2 border-b border-outline-variant bg-primary/5 text-body-sm text-on-surface flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="orion-spinner" aria-hidden />
      <span className="font-semibold">
        {done.size} of {models.length} models finished
      </span>
      {startedAt ? (
        <span className="font-data-mono text-on-surface-variant">
          {formatElapsed(now - startedAt)} elapsed
        </span>
      ) : null}
      {waiting.length ? (
        <span className="text-on-surface-variant">
          Waiting for {waiting.map((m) => modelLabel(m, catalog)).join(", ")}
        </span>
      ) : null}
      <span className="text-on-surface-variant ml-auto">
        Runs in the background: you can leave this page, and the run is saved under Runs.
      </span>
    </div>
  );
}
